<template>
    <div class="py-12">
        <h1 class="text-2xl font-medium tracking-small text-gray-800">
            {{ t("vue.kiosk_ai_skills") }}
        </h1>
        <p class="mt-1 text-sm text-gray-500">
            {{ t("vue.ai_skills_subtitle") }}
        </p>

        <div class="mt-4">
            <inertia-link :href="route('kiosk.ai-skills.create')">
                <AtomicButton size="sm" color="yellow">
                    {{ t("vue.create") }}
                    <template #icon>
                        <PlusCircleIcon class="mr-2 h-6 w-6" />
                    </template>
                </AtomicButton>
            </inertia-link>
        </div>

        <div
            class="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
            <table class="w-full text-left text-sm">
                <thead class="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                        <th class="px-5 py-3">{{ t("vue.name") }}</th>
                        <th class="px-5 py-3">{{ t("vue.ai_skills_slug") }}</th>
                        <th class="px-5 py-3">
                            {{ t("vue.ai_skills_tools") }}
                        </th>
                        <th class="px-5 py-3">
                            {{ t("vue.ai_skills_active") }}
                        </th>
                        <th class="px-5 py-3"></th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr v-if="!skills.length">
                        <td class="px-5 py-6 text-gray-400" colspan="5">
                            {{ t("vue.ai_skills_empty") }}
                        </td>
                    </tr>
                    <tr
                        v-for="skill in skills"
                        :key="skill.id"
                        class="hover:bg-gray-50"
                    >
                        <td class="px-5 py-4">
                            <div class="font-medium text-gray-800">
                                {{ skill.name }}
                            </div>
                            <div
                                v-if="skill.description"
                                class="max-w-md truncate text-xs text-gray-400"
                            >
                                {{ skill.description }}
                            </div>
                        </td>
                        <td class="px-5 py-4">
                            <code class="text-xs text-gray-600">{{
                                skill.slug
                            }}</code>
                        </td>
                        <td class="px-5 py-4 text-gray-500">
                            {{ skill.tool_count }}
                        </td>
                        <td class="px-5 py-4">
                            <span
                                class="rounded px-2 py-1 text-xs font-medium"
                                :class="
                                    skill.is_active
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-500'
                                "
                            >
                                {{
                                    skill.is_active
                                        ? t("vue.ai_skills_on")
                                        : t("vue.ai_skills_off")
                                }}
                            </span>
                        </td>
                        <td class="px-5 py-4">
                            <div class="flex justify-end gap-2">
                                <inertia-link
                                    :href="
                                        route('kiosk.ai-skills.edit', {
                                            aiSkill: skill.id,
                                        })
                                    "
                                >
                                    <AtomicButton size="sm" color="white">
                                        <template #icon>
                                            <PencilSquareIcon class="h-5 w-5" />
                                        </template>
                                    </AtomicButton>
                                </inertia-link>
                                <AtomicButton
                                    size="sm"
                                    color="red"
                                    @click="deleteSkill(skill)"
                                >
                                    <template #icon>
                                        <TrashIcon class="h-5 w-5" />
                                    </template>
                                </AtomicButton>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import useConfirm from "@/composables/useConfirm";
import type { IInternalAiSkillListItem } from "@/types/AiChat/IInternalAiSkill";

defineProps<{
    skills: IInternalAiSkillListItem[];
}>();

const { t } = useTranslations();
const { show } = useConfirm();

const deleteSkill = async (skill: IInternalAiSkillListItem) => {
    const confirmed = await show({
        header: t("vue.delete"),
        message: t("vue.ai_skills_delete_confirm"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(route("kiosk.ai-skills.destroy", { aiSkill: skill.id }), {
            preserveScroll: true,
        });
    }
};
</script>
